import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useProgressSteps } from '@/features/funnel/progress/hooks/useProgressSteps';
import { preQuestionnaireStore } from '@/store/preQuestionnaireStore';
import { productSlugStore } from '@/store/productSlugStore';
import { useFunnelStore } from '@/store/funnelStore';
import { useFetchData } from '@/utils/ReqUtil';
import type { ApiSuccessResponse } from '@/types/api/shared';
import AnimatedProgressBar from './AnimatedProgressBar';

export default function ProgressBar() {
  const { slug: paramSlug, questionnaire_id } = useParams<{
    slug: string;
    questionnaire_id?: string;
  }>();
  const { slug: storeSlug } = productSlugStore();
  const { setShowProgressBar } = useFunnelStore();
  const slug = paramSlug ?? storeSlug;

  // 1. Fetch dynamic question IDs
  const {
    data: qData,
    isLoading,
    isFetching,
  } = useFetchData<ApiSuccessResponse<{ user_questionnaire_id: number; question_ids: number[] }>>({
    url: `/products/${slug}/questions`,
    key: ['questionnaire-progress', slug, questionnaire_id ?? ''],
    retry: 0,
    enabled: !!slug,
  });

  // 2. Pre-questionnaire state
  const { questions: preQQuestions, answers: preQAnswers } = preQuestionnaireStore();
  const preTotal = preQQuestions.length;
  const preAnswered = preQAnswers.length;

  // 3. Build dynamic paths for the main questionnaire steps
  const [dynamicPaths, setDynamicPaths] = useState<string[]>([]);
  useEffect(() => {
    const ids = qData?.data?.question_ids ?? [];
    if (ids.length) {
      setDynamicPaths(
        ids.map(
          (id) =>
            `/products/${slug}/user_questionnaires/${qData?.data.user_questionnaire_id}/questions/${id}`,
        ),
      );
    }
  }, [qData, slug]);

  // 4. Get unified steps + current index
  const { steps, currentStepIndex, showProgressBar } = useProgressSteps(dynamicPaths);

  const totalSteps = steps.length;
  const isPreQStep = steps[currentStepIndex]?.id === 'pre_questionnaire';

  // 5. Compute progressPercentage
  const visualOffset = 0.1; // 10% minimum for the first step
  const stepMaxPercent = totalSteps > 1 ? (1 / (totalSteps - 1)) * 100 : 0;

  let progressPercentage = 0;

  if (isPreQStep && preTotal > 0 && totalSteps > 1) {
    // Interpolate within the first step based on answers
    const fractionAnswered = preAnswered / preTotal;
    const stepFraction = Math.min(1, visualOffset + fractionAnswered * (1 - visualOffset));
    progressPercentage = stepFraction * stepMaxPercent;
  } else if (!isPreQStep && currentStepIndex === 0 && totalSteps > 1) {
    // No preQ, still on first step → give a visual offset
    progressPercentage = visualOffset * stepMaxPercent;
  } else if (currentStepIndex >= 0 && totalSteps > 1) {
    // All other steps: linear progress
    progressPercentage = (currentStepIndex / (totalSteps - 1)) * 100;
  }

  progressPercentage = Math.min(progressPercentage, 100);

  const displayProgressBar = showProgressBar && (!isLoading || !isFetching);

  useEffect(() => {
    setShowProgressBar(displayProgressBar);
  }, [setShowProgressBar, displayProgressBar]);

  return (
    <div>
      {displayProgressBar && (
        <AnimatedProgressBar 
        progressPercentage={progressPercentage}
          className="bg-funnel-bg mx-auto py-[32px] tablet:pt-0 tablet:pb-48 px-6 tablet:px-0"
        />
      )}
    </div>
  );
}
